// src/utils/diagnosticEngine.js
// Deep diagnostic analysis — 7 questions that identify root causes of unprofitability
// Converts raw trade stats into actionable, personalised insights

export function runDiagnostic(trades, settings) {
  const closed   = (trades || []).filter(t => t.status !== 'open' && t.date);
  const startBal = settings?.startingBalance || 0;

  if (closed.length < 10) return null;

  const results = {
    tradeCount: closed.length,
    questions:  [],
    simulation: null,
    plan:       null,
    overallVerdict: null,
  };

  // ── Q1: WHERE ARE YOU LOSING? ─────────────────────────────────
  const q1 = analyseByDimension(closed, startBal);
  results.questions.push(q1);

  // ── Q2: DO YOU HAVE A REAL EDGE? ─────────────────────────────
  const q2 = analyseEdge(closed);
  results.questions.push(q2);

  // ── Q3: WHAT IS YOUR MOST EXPENSIVE HABIT? ───────────────────
  const q3 = analyseWorstHabit(closed, startBal);
  results.questions.push(q3);

  // ── Q4: ARE YOU A GOOD TRADER WITH BAD RISK MANAGEMENT? ──────
  const q4 = analyseRiskVsExecution(closed, startBal);
  results.questions.push(q4);

  // ── Q5: FIXED RISK SIMULATION ────────────────────────────────
  const q5 = simulateFixedRisk(closed, startBal);
  results.questions.push(q5);
  results.simulation = q5;

  // ── Q6: PSYCHOLOGICAL PROFILE ────────────────────────────────
  const q6 = analysePsychology(closed);
  results.questions.push(q6);

  // ── Q7: BREAKEVEN CALCULATION ────────────────────────────────
  const q7 = calculateBreakeven(closed);
  results.questions.push(q7);

  // ── OVERALL VERDICT ──────────────────────────────────────────
  results.overallVerdict = buildVerdict(results.questions, closed, startBal);

  // ── IMPROVEMENT PLAN ─────────────────────────────────────────
  results.plan = buildImprovementPlan(results.questions, closed, startBal);

  return results;
}

// ── Q1: WHERE LOSING? ────────────────────────────────────────────
function analyseByDimension(trades, startBal) {
  const byPair = groupStats(trades, 'pair');
  const bySess = groupStats(trades, 'session');
  const byDir  = groupStats(trades, 'direction');
  const byDay  = {};

  for (const t of trades) {
    if (!t.date) continue;
    const day = new Date(t.date).toLocaleDateString('en-US', { weekday: 'long' });
    if (!byDay[day]) byDay[day] = { pnl:0, wins:0, total:0 };
    byDay[day].total++;
    byDay[day].pnl += Number(t.pnl||0);
    if (t.result === 'Win') byDay[day].wins++;
  }

  // Find biggest P&L drag
  const pairList = Object.entries(byPair).filter(([,s]) => s.count >= 3)
    .map(([k,s]) => ({ name:k, ...s, wr: Math.round(s.wins/s.count*100) }))
    .sort((a,b) => a.pnl - b.pnl);

  const worstPair = pairList[0];
  const bestPair  = pairList[pairList.length-1];

  const sessList = Object.entries(bySess).filter(([,s]) => s.count >= 3)
    .map(([k,s]) => ({ name:k, ...s, wr: Math.round(s.wins/s.count*100) }))
    .sort((a,b) => a.pnl - b.pnl);

  const worstSess = sessList[0];
  const bestSess  = sessList[sessList.length-1];

  const dayList = Object.entries(byDay)
    .map(([day, s]) => ({ day, ...s, wr: Math.round(s.wins/s.total*100) }))
    .sort((a,b) => a.pnl - b.pnl);

  const worstDay = dayList[0];

  const findings = [];
  if (worstPair && worstPair.pnl < 0)
    findings.push({ severity:'high', text:`${worstPair.name}: ${worstPair.wr}% WR · $${worstPair.pnl.toFixed(0)} total loss on ${worstPair.count} trades` });
  if (bestPair && bestPair.pnl > 0)
    findings.push({ severity:'positive', text:`${bestPair.name}: ${bestPair.wr}% WR · +$${bestPair.pnl.toFixed(0)} total on ${bestPair.count} trades` });
  if (worstSess && worstSess.pnl < 0)
    findings.push({ severity:'medium', text:`${worstSess.name} session: ${worstSess.wr}% WR · $${worstSess.pnl.toFixed(0)}` });
  if (worstDay && worstDay.pnl < 0)
    findings.push({ severity:'medium', text:`${worstDay.day}s: ${worstDay.wr}% WR · $${worstDay.pnl.toFixed(0)} total` });

  const verdict = worstPair?.pnl < -100
    ? `Your biggest drain is ${worstPair.name}. Stopping this one instrument would save $${Math.abs(worstPair.pnl).toFixed(0)}.`
    : worstSess?.pnl < -100
    ? `Your ${worstSess.name} session is dragging your results.`
    : 'Your losses are distributed across multiple areas.';

  return { id:'q1', question:'Where exactly are you losing money?', verdict, findings, status: worstPair?.pnl < 0 ? 'warning' : 'ok', pairStats: pairList, sessStats: sessList };
}

// ── Q2: REAL EDGE? ───────────────────────────────────────────────
function analyseEdge(trades) {
  const wins     = trades.filter(t => t.result === 'Win');
  const total    = trades.length;
  const winRate  = wins.length / total;
  const avgWin   = avg(wins.map(t => Number(t.pnl||0)));
  const losses   = trades.filter(t => t.result === 'Loss');
  const avgLoss  = Math.abs(avg(losses.map(t => Number(t.pnl||0))));
  const ev       = (winRate * avgWin) - ((1-winRate) * avgLoss);

  // Statistical significance — chi-squared approximation
  const expected = total * 0.5;
  const chiSq    = Math.pow(wins.length - expected, 2) / expected + Math.pow(losses.length - expected, 2) / expected;
  const significant = chiSq > 3.84 && total >= 20; // p < 0.05

  const findings = [
    { severity: winRate > 0.5 ? 'positive' : 'warning', text:`Win rate: ${Math.round(winRate*100)}% across ${total} trades` },
    { severity: ev > 0 ? 'positive' : 'warning', text:`Expected value: ${ev >= 0 ? '+' : ''}$${ev.toFixed(2)} per trade` },
    { severity: significant ? 'positive' : 'medium', text: significant ? 'Statistically significant (p<0.05) — your edge is real' : 'Not yet statistically significant — need more trades or larger edge' },
  ];

  const verdict = ev > 0 && significant
    ? `You have a real, statistically significant edge. EV of +$${ev.toFixed(2)} per trade.`
    : ev > 0 && !significant
    ? `Positive EV but not yet statistically confirmed. Keep logging trades.`
    : `Negative expected value. You are losing money in the long run. This must be addressed first.`;

  return { id:'q2', question:'Do you have a real statistical edge?', verdict, findings, status: ev > 0 ? 'ok' : 'critical', ev, winRate: Math.round(winRate*100) };
}

// ── Q3: MOST EXPENSIVE HABIT ─────────────────────────────────────
function analyseWorstHabit(trades, startBal) {
  const costs = [];

  // Rule breaks cost
  const breaks = trades.filter(t => t.followedRules === false || t.followedRules === 'false');
  const rulesOk = trades.filter(t => t.followedRules === true || t.followedRules === 'true');
  if (breaks.length >= 3 && rulesOk.length >= 3) {
    const breakPnl = breaks.reduce((s,t) => s+Number(t.pnl||0), 0);
    const ruleWR   = Math.round(rulesOk.filter(t=>t.result==='Win').length/rulesOk.length*100);
    const breakWR  = Math.round(breaks.filter(t=>t.result==='Win').length/breaks.length*100);
    if (breakPnl < 0) costs.push({ habit:'Rule breaks', cost: Math.abs(breakPnl), detail:`${breaks.length} rule breaks cost $${Math.abs(breakPnl).toFixed(0)} · WR: ${breakWR}% vs ${ruleWR}% when following rules` });
  }

  // Overtrading cost
  const byDate = {};
  for (const t of trades) { if (!t.date) continue; if (!byDate[t.date]) byDate[t.date]=[]; byDate[t.date].push(t); }
  const allDays    = Object.values(byDate);
  const avgPerDay  = avg(allDays.map(d=>d.length));
  const heavyDays  = allDays.filter(d => d.length > avgPerDay * 1.8);
  if (heavyDays.length >= 3) {
    const heavyPnl = heavyDays.reduce((s,d) => s + d.reduce((a,t)=>a+Number(t.pnl||0),0), 0);
    if (heavyPnl < 0) costs.push({ habit:'Overtrading', cost: Math.abs(heavyPnl), detail:`High-volume days cost $${Math.abs(heavyPnl).toFixed(0)} total` });
  }

  // Mistake costs
  const mistakes = trades.filter(t => t.mistakeMade && t.mistakeMade !== 'None');
  if (mistakes.length >= 3) {
    const mistakePnl = mistakes.reduce((s,t) => s+Number(t.pnl||0), 0);
    if (mistakePnl < 0) costs.push({ habit:'Flagged mistakes', cost: Math.abs(mistakePnl), detail:`${mistakes.length} trades with mistakes cost $${Math.abs(mistakePnl).toFixed(0)}` });
  }

  costs.sort((a,b) => b.cost - a.cost);
  const worst = costs[0];

  return {
    id: 'q3',
    question: 'What is your single most expensive habit?',
    verdict: worst ? `Your most expensive habit is "${worst.habit}" costing $${worst.cost.toFixed(0)}.` : 'No single dominant costly habit identified yet.',
    findings: costs.map(c => ({ severity:'warning', text:`${c.habit}: $${c.cost.toFixed(0)} cost · ${c.detail}` })),
    status: worst ? 'warning' : 'ok',
    costs,
  };
}

// ── Q4: GOOD TRADER BAD RISK? ────────────────────────────────────
function analyseRiskVsExecution(trades, startBal) {
  const wins    = trades.filter(t => t.result === 'Win');
  const losses  = trades.filter(t => t.result === 'Loss');
  const avgWin  = avg(wins.map(t => Number(t.pnl||0)));
  const avgLoss = Math.abs(avg(losses.map(t => Number(t.pnl||0))));
  const winRate = trades.length > 0 ? wins.length / trades.length : 0;

  // Check loss sizing consistency
  const lossPnls = losses.map(t => Math.abs(Number(t.pnl||0))).filter(v=>v>0);
  const maxLoss  = lossPnls.length ? Math.max(...lossPnls) : 0;
  const minLoss  = lossPnls.length ? Math.min(...lossPnls) : 0;
  const lossSizeRange = maxLoss > 0 ? maxLoss / Math.max(minLoss, 1) : 1;

  const executionGood = winRate >= 0.5;
  const riskBad = lossSizeRange > 5 || (avgLoss > avgWin * 1.5);

  const findings = [
    { severity: executionGood ? 'positive' : 'warning', text:`Trade selection: ${Math.round(winRate*100)}% WR — ${executionGood ? 'good' : 'needs work'}` },
    { severity: riskBad ? 'warning' : 'positive', text:`Loss sizing: max loss is ${lossSizeRange.toFixed(1)}x min loss — ${riskBad ? 'inconsistent' : 'consistent'}` },
    { severity: avgLoss > avgWin ? 'warning' : 'positive', text:`Avg win $${avgWin.toFixed(0)} vs avg loss $${avgLoss.toFixed(0)} — ratio ${(avgWin/Math.max(avgLoss,1)).toFixed(2)}` },
  ];

  let verdict;
  if (executionGood && riskBad) verdict = 'You are a good trader with poor risk management. Your trade selection works but position sizing is inconsistent.';
  else if (executionGood && !riskBad) verdict = 'Both execution and risk management are solid. Focus on increasing trade frequency within your edge.';
  else if (!executionGood && !riskBad) verdict = 'Risk management is consistent but trade selection needs improvement. Your setups need refinement.';
  else verdict = 'Both execution and risk management need work. Start with fixing your position sizing — it is the fastest fix.';

  return { id:'q4', question:'Are you a good trader with bad risk management, or struggling overall?', verdict, findings, status: riskBad ? 'warning' : 'ok', executionGood, riskBad };
}

// ── Q5: FIXED RISK SIMULATION ────────────────────────────────────
function simulateFixedRisk(trades, startBal) {
  if (!startBal || startBal === 0) {
    return { id:'q5', question:'What would your results look like with fixed 1% risk per trade?', verdict:'Set your starting balance to run this simulation.', findings:[], status:'info' };
  }

  const riskPct    = 0.01; // 1%
  const riskAmount = startBal * riskPct;

  let simBalance = startBal;
  let realBalance = startBal;
  const sorted = [...trades].sort((a,b) => new Date(a.date)-new Date(b.date));

  for (const t of sorted) {
    const realPnl = Number(t.pnl || 0);
    realBalance += realPnl;

    // Simulate: if they risked exactly 1% per trade
    // Scale their actual P&L proportionally based on 1% risk
    const actualRisk = t.riskAmount ? Number(t.riskAmount) : Math.abs(realPnl);
    const scale = actualRisk > 0 ? riskAmount / actualRisk : 1;
    const simPnl = realPnl * Math.min(scale, 3); // cap at 3x to avoid outlier distortion
    simBalance += simPnl;
  }

  const realReturn = startBal > 0 ? ((realBalance - startBal) / startBal * 100).toFixed(1) : '0';
  const simReturn  = startBal > 0 ? ((simBalance  - startBal) / startBal * 100).toFixed(1) : '0';
  const better = Number(simReturn) > Number(realReturn);

  const verdict = better
    ? `With fixed 1% risk per trade, your account would be at ${simReturn}% instead of ${realReturn}%. Consistent sizing would ${Number(simReturn) > 0 ? 'make you profitable' : 'reduce your losses significantly'}.`
    : `Your current sizing is already close to optimal. Fixed 1% risk shows ${simReturn}% vs your actual ${realReturn}%.`;

  return {
    id: 'q5',
    question: 'What would your results look like with fixed 1% risk per trade?',
    verdict,
    findings: [
      { severity: 'medium', text:`Your actual result: ${realReturn}%` },
      { severity: better ? 'positive' : 'medium', text:`Fixed 1% simulation: ${simReturn}%` },
      { severity: 'medium', text:`Difference: ${(Number(simReturn)-Number(realReturn)).toFixed(1)}%` },
    ],
    status: better ? 'warning' : 'ok',
    realReturn, simReturn,
  };
}

// ── Q6: PSYCHOLOGICAL PROFILE ────────────────────────────────────
function analysePsychology(trades) {
  const withEmotion = trades.filter(t => t.emotionBefore);
  if (withEmotion.length < 5) {
    return { id:'q6', question:'What does your psychological profile say?', verdict:'Log your emotional state on more trades for psychological analysis.', findings:[{ severity:'info', text:'Need 5+ trades with emotion data logged' }], status:'info' };
  }

  const byEmotion = {};
  for (const t of withEmotion) {
    const e = t.emotionBefore.toLowerCase();
    if (!byEmotion[e]) byEmotion[e] = { wins:0, total:0, pnl:0 };
    byEmotion[e].total++;
    byEmotion[e].pnl += Number(t.pnl||0);
    if (t.result === 'Win') byEmotion[e].wins++;
  }

  const emotionStats = Object.entries(byEmotion)
    .filter(([,s]) => s.total >= 2)
    .map(([emotion, s]) => ({ emotion, ...s, wr: Math.round(s.wins/s.total*100) }))
    .sort((a,b) => b.wr - a.wr);

  const bestEmotion  = emotionStats[0];
  const worstEmotion = emotionStats[emotionStats.length-1];

  const findings = emotionStats.map(e => ({
    severity: e.wr >= 55 ? 'positive' : e.wr <= 35 ? 'warning' : 'medium',
    text: `${e.emotion}: ${e.wr}% WR across ${e.total} trades · ${e.pnl >= 0 ? '+' : ''}$${e.pnl.toFixed(0)}`,
  }));

  const verdict = bestEmotion && worstEmotion && bestEmotion !== worstEmotion
    ? `You perform best when ${bestEmotion.emotion} (${bestEmotion.wr}% WR) and worst when ${worstEmotion.emotion} (${worstEmotion.wr}% WR). ${worstEmotion.wr <= 35 ? `Do not trade when ${worstEmotion.emotion}.` : ''}`
    : 'Your performance is consistent across emotional states.';

  return { id:'q6', question:'What does your psychological profile reveal?', verdict, findings, status: worstEmotion?.wr <= 35 ? 'warning' : 'ok', emotionStats };
}

// ── Q7: BREAKEVEN ────────────────────────────────────────────────
function calculateBreakeven(trades) {
  const wins    = trades.filter(t => t.result === 'Win');
  const losses  = trades.filter(t => t.result === 'Loss');
  const avgWin  = avg(wins.map(t => Number(t.pnl||0)));
  const avgLoss = Math.abs(avg(losses.map(t => Number(t.pnl||0))));
  const currentWR = trades.length > 0 ? Math.round(wins.length/trades.length*100) : 0;

  // WR needed to break even at current avg win/loss
  const wrNeeded = avgLoss > 0 && avgWin > 0 ? Math.ceil((avgLoss / (avgWin + avgLoss)) * 100) : 50;
  const rrNeeded = avgLoss > 0 ? (avgLoss / Math.max(avgWin, 1)).toFixed(2) : '1.0';
  const isBreakeven = currentWR >= wrNeeded;

  const ruleBreaks = trades.filter(t => t.followedRules === false || t.followedRules === 'false').length;
  const monthsOfRuleBreaks = ruleBreaks / Math.max(1, trades.length / 20); // approx months

  const findings = [
    { severity: isBreakeven ? 'positive' : 'warning', text:`You need ${wrNeeded}% WR to break even at your current avg win/loss. You currently have ${currentWR}%.` },
    { severity: 'medium', text:`OR average RR above ${rrNeeded}R per trade` },
    { severity: ruleBreaks > 0 ? 'warning' : 'positive', text:`OR eliminate your ${ruleBreaks} rule breaks per analysis period` },
  ];

  return {
    id: 'q7',
    question: 'What exactly needs to change to reach profitability?',
    verdict: isBreakeven
      ? `You are above breakeven. Focus on scaling consistency and increasing position quality.`
      : `You need to either reach ${wrNeeded}% WR, achieve ${rrNeeded}R average, or stop the ${ruleBreaks} rule breaks. Any one of these three changes makes you profitable.`,
    findings,
    status: isBreakeven ? 'ok' : 'warning',
    wrNeeded, currentWR, rrNeeded, ruleBreaks,
  };
}

// ── BUILD IMPROVEMENT PLAN ────────────────────────────────────────
export function buildImprovementPlan(questions, trades, startBal) {
  const focusAreas = [];
  const q = {};
  for (const question of questions) q[question.id] = question;

  // Pick the 3 most impactful focus areas based on what the data shows
  // Priority: critical > warning > ok

  if (q.q2?.status === 'critical') {
    focusAreas.push({
      id: 'edge',
      title: 'Fix your expected value',
      icon: '',
      target: 'Achieve positive EV (+$0.50+ per trade average)',
      action: `Your current EV is negative meaning you lose money long-term. Focus exclusively on your highest win-rate setup for the next 30 days.`,
      metric: 'EV per trade',
      current: q.q2.ev?.toFixed(2) || '0',
      target_val: '+0.50',
    });
  }

  if (q.q1?.status === 'warning' && q.q1.pairStats?.[0]?.pnl < 0) {
    const worst = q.q1.pairStats[0];
    focusAreas.push({
      id: 'instrument',
      title: `Stop trading ${worst.name}`,
      icon: '',
      target: `Zero trades on ${worst.name} for 30 days`,
      action: `You have no statistical edge on ${worst.name} (${worst.wr}% WR). Stopping this instrument immediately saves $${Math.abs(worst.pnl).toFixed(0)}.`,
      metric: `${worst.name} trade count`,
      current: String(worst.count),
      target_val: '0',
    });
  }

  if (q.q3?.costs?.[0]) {
    const worst = q.q3.costs[0];
    focusAreas.push({
      id: 'habit',
      title: `Eliminate: ${worst.habit}`,
      icon: '',
      target: `Reduce ${worst.habit} cost by 80%`,
      action: worst.detail,
      metric: `${worst.habit} cost`,
      current: `$${worst.cost.toFixed(0)}`,
      target_val: `$${(worst.cost * 0.2).toFixed(0)}`,
    });
  }

  if (q.q4?.riskBad && focusAreas.length < 3) {
    focusAreas.push({
      id: 'risk',
      title: 'Fix position sizing consistency',
      icon: '',
      target: 'Risk exactly 1% per trade for 30 days',
      action: 'Your loss sizes vary wildly. Set a fixed risk amount per trade and do not deviate. Start with $50 or 1% — whichever is smaller.',
      metric: 'Max/min loss ratio',
      current: 'Inconsistent',
      target_val: '< 2x variance',
    });
  }

  if (q.q6?.emotionStats?.length > 0 && q.q6.emotionStats[q.q6.emotionStats.length-1]?.wr <= 35 && focusAreas.length < 3) {
    const worst = q.q6.emotionStats[q.q6.emotionStats.length-1];
    focusAreas.push({
      id: 'emotion',
      title: `No trading when ${worst.emotion}`,
      icon: '',
      target: `Zero trades logged with "${worst.emotion}" state`,
      action: `Your win rate when ${worst.emotion} is only ${worst.wr}%. This emotional state is costing you money. Add a rule: no trading when ${worst.emotion}.`,
      metric: `Trades when ${worst.emotion}`,
      current: String(worst.total),
      target_val: '0',
    });
  }

  // Always have 3 focus areas — fill with generic high-value ones if needed
  if (focusAreas.length < 3) {
    const { won, total } = { won: trades.filter(t=>t.result==='Win').length, total: trades.length };
    const wr = Math.round(won/total*100);
    if (wr < 50) focusAreas.push({
      id: 'winrate',
      title: 'Raise win rate to 50%+',
      icon: '',
      target: '50%+ win rate over next 20 trades',
      action: 'Only take trades that match all your setup criteria. When in doubt, do not enter.',
      metric: 'Win rate', current: `${wr}%`, target_val: '50%+',
    });
  }

  return {
    focusAreas: focusAreas.slice(0, 3),
    createdAt:  new Date().toISOString(),
    tradeCount: trades.length,
  };
}

// ── VERDICT ──────────────────────────────────────────────────────
function buildVerdict(questions, trades, startBal) {
  const criticals = questions.filter(q => q.status === 'critical').length;
  const warnings  = questions.filter(q => q.status === 'warning').length;
  const wins      = trades.filter(t => t.result === 'Win').length;
  const wr        = Math.round(wins/trades.length*100);
  const totalPnl  = trades.reduce((s,t) => s+Number(t.pnl||0), 0);

  if (criticals > 0) return { level:'critical', color:'#ff4d6d', label:'Needs Immediate Attention', summary:`You have ${criticals} critical issue${criticals>1?'s':''} that must be addressed before anything else.` };
  if (warnings  > 2) return { level:'warning',  color:'#ffd166', label:'Developing',  summary:`${warnings} areas need attention. Your fundamentals are there but consistency is missing.` };
  if (totalPnl  > 0 && wr >= 50) return { level:'good', color:'#00e5a0', label:'Profitable', summary:'You are profitable. Focus is on scaling your edge and reducing your worst habits.' };
  return { level:'moderate', color:'#ff9f43', label:'Building Edge', summary:'You have some strengths but key weaknesses are preventing consistent profitability.' };
}

// ── HELPERS ──────────────────────────────────────────────────────
function groupStats(trades, field) {
  const g = {};
  for (const t of trades) {
    const k = t[field] || 'Unknown';
    if (!g[k]) g[k] = { wins:0, total:0, pnl:0, count:0 };
    g[k].total++; g[k].count++;
    g[k].pnl += Number(t.pnl||0);
    if (t.result === 'Win') g[k].wins++;
  }
  return g;
}

function avg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a,b) => a+b, 0) / arr.length;
}
